import { useState } from 'react';
import { RefreshCcw, TriangleAlert } from 'lucide-react';
import { Button, DataTable, ErrorState, FormField, PageHeader, Panel, Select, StatusBadge, useConfirm, useToast, type Column } from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { ApiError, errorMessage } from '@/lib/api';
import { formatDateTime, formatNumber } from '@/lib/format';
import { fiscalYearLabel } from '@/lib/wareki';
import { useAnnualUpdateRuns, useRunAnnualUpdate, type AnnualUpdateResult, type AnnualUpdateRun } from './api';
import './admin.css';

const RESULT_LABELS: [keyof AnnualUpdateResult, string][] = [
  ['operatorSettings', '事業実施者 年度設定'],
  ['charterFees', '用船料'],
  ['wages', '賃金'],
  ['zones', '農林漁区番号表'],
  ['limits', '年間限度調査日数'],
  ['limitExtras', 'プラス日数'],
  ['ports', '港名'],
  ['vessels', '参加船'],
  ['captains', '船長'],
  ['crew', '乗組員'],
  ['capacities', '乗船人数'],
  ['fishingMethods', '漁業種類'],
];

/** S-90 年次更新処理（基金のみ） */
export function AnnualUpdatePage() {
  const { year, years } = useFiscalYear();
  const toast = useToast();
  const confirm = useConfirm();
  const runs = useAnnualUpdateRuns();
  const run = useRunAnnualUpdate();
  const [source, setSource] = useState<number>(year);
  const [target, setTarget] = useState<number | null>(year + 1);
  const [result, setResult] = useState<{ source: number; target: number; r: AnnualUpdateResult } | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);

  const targetExists = target != null && years.some((y) => y.year === target);

  const onRun = async () => {
    setBlocked(null);
    if (target == null || target < 2000 || target > 2100) {
      toast.error('実行できません', '作成する年度を西暦で入力してください。');
      return;
    }
    if (target === source) {
      toast.error('実行できません', '元の年度と作成する年度には異なる年度を指定してください。');
      return;
    }
    const ok = await confirm({
      title: '年次更新処理を実行しますか？',
      message:
        `${fiscalYearLabel(source)}（${source}年度）の年度別マスタ・港名・参加船を、${fiscalYearLabel(target)}（${target}年度）へ引き継ぎます。\n\n` +
        `・${target}年度に既にある年度別マスタ・港名・参加船は削除され、置き換わります。\n` +
        `・参加船は ${source + 1}年3月31日時点で有効なものだけを、参加期間 ${target}年4月1日〜${target + 1}年3月31日で引き継ぎます。\n` +
        `・計画日数は 0 に初期化されます。`,
      tone: 'danger',
      confirmLabel: '実行する',
    });
    if (!ok) return;
    try {
      const r = await run.mutateAsync({ sourceYear: source, targetYear: target });
      setResult({ source, target, r: r.result });
      toast.success('年次更新処理が完了しました', `${target}年度へ参加船 ${r.result.vessels} 隻を引き継ぎました`);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'ANNUAL_UPDATE_TARGET_IN_USE') setBlocked(e.message);
      toast.error('実行できませんでした', errorMessage(e));
    }
  };

  const columns: Column<AnnualUpdateRun>[] = [
    { key: 'executedAt', header: '実行日時', width: 170, render: (r) => <span className="bs-num">{formatDateTime(r.executedAt)}</span> },
    { key: 'years', header: '元の年度 → 作成した年度', width: 220, render: (r) => `${r.sourceYear}年度 → ${r.targetYear}年度` },
    { key: 'executedByName', header: '実行者', width: 160, render: (r) => r.executedByName ?? '—' },
    {
      key: 'result',
      header: '引継ぎ件数',
      render: (r) => (
        <span className="bs-num">
          参加船 {formatNumber(r.result.vessels ?? 0)} ／ 船長 {formatNumber(r.result.captains ?? 0)} ／ 乗組員 {formatNumber(r.result.crew ?? 0)} ／ 乗船人数 {formatNumber(r.result.capacities ?? 0)} ／ 港名 {formatNumber(r.result.ports ?? 0)}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader eyebrow="システム管理" title="年次更新処理" description="新しい事業年度を作成し、前年度のマスタ・港名・参加船を引き継ぎます。年度の切替時に基金が1回実行します。" />

      <Panel title="実行条件" description="作成する年度が未登録の場合は、事業年度も自動で作成されます。">
        <div className="bs-annual">
          <FormField label="元の年度" required>
            <Select width="md" value={source} onChange={(e) => setSource(Number(e.target.value))} options={years.map((y) => ({ value: y.year, label: `${y.label}（${y.year}年度）` }))} />
          </FormField>
          <span className="bs-annual__arrow" aria-hidden>→</span>
          <FormField label="作成する年度" required>
            <Select
              width="md"
              value={target ?? ''}
              onChange={(e) => setTarget(Number(e.target.value))}
              options={[...years.map((y) => ({ value: y.year, label: `${y.label}（${y.year}年度）` })), ...[1, 2].map((n) => { const y = Math.max(...years.map((x) => x.year)) + n; return { value: y, label: `${fiscalYearLabel(y)}（${y}年度）　新規作成` }; })].sort((a, b) => a.value - b.value)}
            />
          </FormField>
          <Button variant="danger" icon={<RefreshCcw size={16} />} onClick={onRun} loading={run.isPending}>
            年次更新を実行
          </Button>
        </div>
        {/* 補足は入力欄の下に1行で表示（欄ごとに高さが変わって横並びがずれないように FormField の hint は使わない） */}
        <p className={`bs-annual__hint${targetExists ? ' is-warn' : ''}`}>
          作成する年度は{targetExists ? '登録済みです。既存データは置き換わります。' : '未登録です。新規に作成します。'}
        </p>
        {blocked && (
          <div className="bs-annual__alert" role="alert">
            <TriangleAlert size={16} aria-hidden /> {blocked}
          </div>
        )}
      </Panel>

      {result && (
        <Panel title={`実行結果：${result.source}年度 → ${result.target}年度`} actions={<StatusBadge tone="success" dot>完了</StatusBadge>}>
          <dl className="bs-annual__result">
            {RESULT_LABELS.map(([k, label]) => (
              <div key={k}>
                <dt>{label}</dt>
                <dd className="bs-num">{formatNumber(result.r[k])}</dd>
              </div>
            ))}
          </dl>
          <p className="bs-annual__note">年間限度調査日数のプラス日数の対象期間は元の年度のまま複製されます。マスタ管理で新年度の期間に修正してください。</p>
        </Panel>
      )}

      <Panel title="実行履歴" flush>
        {runs.error ? (
          <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />
        ) : (
          <DataTable caption="年次更新処理の実行履歴" columns={columns} rows={runs.data ?? []} rowKey={(r) => r.id} loading={runs.isPending} dense empty="実行履歴はありません" pagination={{ pageSize: 10 }} />
        )}
      </Panel>
    </>
  );
}
